import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Input } from 'reactstrap';
import * as actions from '../../actions';
import { Field, reduxForm, InjectedFormProps } from 'redux-form';
import InputGroup from '../Forms/InputGroup';
import approve from 'approvejs';
import { getTranslationKey } from '../../modules/utils/helpers';
import type { Notify } from '../../typings/redux';
import styled from 'styled-components';

const StyledFormGroup = styled(FormGroup)`
  margin-bottom: 0 !important;
`;

interface FieldConfig {
  type: string;
  placeholder: string;
  tooltip_id: string;
  tooltip_text: string;
  title: string;
  rules: {
    required?: { message: string };
  };
}

interface FieldsConfig {
  description: FieldConfig;
  url: FieldConfig;
  imprint: FieldConfig;
}

const getFields = (): FieldsConfig => ({
  description: {
    type: 'text',
    placeholder: getTranslationKey('description'),
    tooltip_id: 'imprintDescription',
    tooltip_text: getTranslationKey('customDomains.descriptionTooltip'),
    title: getTranslationKey('description'),
    rules: {
      required: { message: getTranslationKey('requiredInput') }
    }
  },
  url: {
    type: 'text',
    placeholder: getTranslationKey('customDomains.url'),
    tooltip_id: 'urlToolTip',
    tooltip_text: getTranslationKey('customDomains.urlToolTip'),
    title: getTranslationKey('customDomains.url'),
    rules: {}
  },
  imprint: {
    type: 'textarea',
    placeholder: getTranslationKey('customDomains.imprint'),
    tooltip_id: 'imprintTooltip',
    tooltip_text: getTranslationKey('customDomains.imprintTooltip'),
    title: getTranslationKey('customDomains.imprint'),
    rules: {}
  }
});

interface ImprintItem {
  _id: string;
  description: string;
  url?: string;
  imprint?: string;
}

interface ImprintsState {
  all: ImprintItem[];
}

interface ImprintFormValues {
  description: string;
  url?: string;
  imprint?: string;
}

interface ImprintOwnProps {
  mode?: 'edit';
  selectedImprint?: string;
  onSelectedImprintChanged?: (imprintId: string) => void;
  domain?: string;
  imprints: ImprintsState;
  notify: Notify;
  createImprint: (values: ImprintFormValues, notify: Notify) => void;
  editImprint: (values: ImprintFormValues & { _id: string }, notify: Notify) => void;
  deleteImprint: (id: string, notify: Notify) => void;
  updateDomain: (domain: { _id: string; imprint: string }) => void;
}

type ImprintProps = ImprintOwnProps & InjectedFormProps<ImprintFormValues, ImprintOwnProps>;

interface ImprintState {
  isOpen: boolean;
  selectedImprint: string;
  currentImprintId?: string;
}

class Imprint extends Component<ImprintProps, ImprintState> {
  constructor(props: ImprintProps) {
    super(props);

    this.state = {
      isOpen: false,
      selectedImprint: props.selectedImprint || 'select'
    };

    this.toggle = this.toggle.bind(this);
    this.submit = this.submit.bind(this);
    this.onSelectImprint = this.onSelectImprint.bind(this);
    this.delete = this.delete.bind(this);
  }

  onSelectImprint({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
    if (this.props.mode === 'edit') {
      const currentImprint = this.props.imprints.all.filter((imprint) => imprint._id === value)[0];
      if (currentImprint) {
        this.setState({ currentImprintId: value });
        this.props.change('description', currentImprint.description);
        this.props.change('url', currentImprint.url);
        this.props.change('imprint', currentImprint.imprint);
        this.toggle();
      }
    } else {
      if (value === 'new') {
        this.toggle();
      } else {
        this.setState({ selectedImprint: value }, () => {
          const { onSelectedImprintChanged, selectedImprint, domain } = this.props;
          if (onSelectedImprintChanged) {
            onSelectedImprintChanged(this.state.selectedImprint);
          }
          if (selectedImprint && domain) {
            this.props.updateDomain({
              _id: domain,
              imprint: this.state.selectedImprint
            });
          }
        });
      }
    }
  }

  toggle() {
    this.setState(
      (prevState) => ({ isOpen: !prevState.isOpen }),
      () => {
        if (!this.state.isOpen) {
          this.props.reset();
        }
      }
    );
  }

  submit(values: ImprintFormValues) {
    if (this.props.mode === 'edit') {
      this.props.editImprint({ ...values, _id: this.state.currentImprintId! }, this.props.notify);
    } else {
      this.props.createImprint(values, this.props.notify);
    }
    this.toggle();
  }

  delete() {
    this.props.deleteImprint(this.state.currentImprintId!, this.props.notify);
    this.toggle();
  }

  render() {
    const imprints = this.props.imprints.all;
    const { mode } = this.props;
    const label =
      mode === 'edit'
        ? getTranslationKey('customDomains.editImprint')
        : getTranslationKey('customDomains.createImprint');

    return (
      <div className="inline">
        <StyledFormGroup>
          <div>
            <Input
              type="select"
              name="product"
              value={this.state.selectedImprint}
              onChange={this.onSelectImprint}
            >
              {mode === 'edit' && (
                <option value="select" disabled>
                  {getTranslationKey('customDomains.editImprint')}
                </option>
              )}
              {!mode && (
                <option value="select" disabled>
                  {getTranslationKey('pleaseSelect')}
                </option>
              )}
              {imprints.map((imprint, key) => (
                <option value={imprint._id} key={key}>
                  {imprint.description}
                </option>
              ))}
            </Input>
          </div>
        </StyledFormGroup>
        <Modal
          centered
          isOpen={this.state.isOpen}
          toggle={this.toggle}
          wrapClassName="modal-primary"
          size="default"
        >
          <ModalHeader toggle={this.toggle}>{label}</ModalHeader>
          <ModalBody>
            <div className="row">
              <div className="col-lg-12">
                <form onSubmit={this.props.handleSubmit(this.submit)}>
                  {Object.keys(getFields()).map((fieldKey, index) => (
                    <Field
                      component={InputGroup}
                      key={index}
                      name={fieldKey}
                      field={getFields()[fieldKey as keyof FieldsConfig]}
                    />
                  ))}

                  <button
                    className="btn btn-primary w-100-p"
                    type="submit"
                    disabled={this.props.pristine || this.props.invalid || this.props.submitting}
                  >
                    {label}
                  </button>
                </form>
                <a
                  href="http://www.impressum-generator.de/impressum-generator/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getTranslationKey('customDomains.imprintGenerator')}
                </a>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            {mode === 'edit' && (
              <small className="cursor-pointer " onClick={this.delete}>
                <i className="sli-trash m-r-5" />
                {`${getTranslationKey('imprint')} ${getTranslationKey('delete')}`}
              </small>
            )}
            <button className="btn btn-danger pull-left" onClick={this.toggle}>
              {getTranslationKey('abort')}
            </button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

function validate(values: ImprintFormValues) {
  const errors: any = {};

  if (!values.url && !values.imprint) {
    const error = [getTranslationKey('minOneRequired')];
    errors.imprint = error;
    errors.url = error;
  }

  Object.keys(getFields()).forEach((fieldKey) => {
    const result = approve.value(
      values[fieldKey as keyof ImprintFormValues],
      getFields()[fieldKey as keyof FieldsConfig].rules
    );
    if (result.errors.length > 0) {
      errors[fieldKey] = result.errors;
    }
  });

  return errors;
}

interface RootState {
  imprints: ImprintsState;
  notify: Notify;
}

function mapStateToProps({ imprints, notify }: RootState) {
  return { imprints, notify };
}

const ImprintWithForm = reduxForm<ImprintFormValues, ImprintOwnProps>({
  form: 'imprintCreationForm',
  validate
})(Imprint);

export default connect(mapStateToProps, actions)(ImprintWithForm);
